import { useCallback, useEffect, useState } from "react";

import type { DriverManifest } from "@/lib/logistics/driverTypes";
import { loadDriverManifestWithCache, stampDriverStopLocal } from "@/lib/driver/driverSync";

export function useDriverApp() {
  const [manifest, setManifest] = useState<DriverManifest | null>(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [stampingStopId, setStampingStopId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [offline, setOffline] = useState(() => !navigator.onLine);
  const [fromCache, setFromCache] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setSyncing(true);
    setError(null);
    const result = await loadDriverManifestWithCache();
    setManifest(result.manifest);
    setFromCache(result.fromCache);
    if (result.error) setError(result.error);
    if (result.conflicts.length) {
      setToast("Some changes were updated from the server. Review your stops.");
    }
    setSyncing(false);
    setLoading(false);
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    const onOnline = () => {
      setOffline(false);
      void refresh();
    };
    const onOffline = () => setOffline(true);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, [refresh]);

  const stamp = useCallback(
    async (stopId: string, field: "arrived_at" | "departed_at") => {
      if (!manifest) return;
      setStampingStopId(stopId);
      setToast(null);
      const result = await stampDriverStopLocal({ manifest, stopId, field });
      setManifest(result.manifest);
      if (result.error) setError(result.error);
      if (result.conflicts.length) {
        setToast("Server has newer data for that stop. Stamp again if needed.");
      }
      setStampingStopId(null);
    },
    [manifest]
  );

  return {
    manifest,
    loading,
    syncing,
    stampingStopId,
    error,
    offline,
    fromCache,
    toast,
    refresh,
    stamp,
    clearToast: () => setToast(null),
  };
}
